import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Modal,
  Card,
  Tooltip,
  TextField,
  withStyles,
  formatAmount,
  ElementContainer,
  sanitizeToNumeric,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { Title, Button, CircleButton } from 'UI';
import { TransferType } from '@/components/Accounts/AccountSummary/AccountSummaryPage';
import { useToast } from '@/providers/Toast.provider';
import usePostInterestAccountTransfer from '@/hooks/useApi/usePostInterestAccountTransfer';
import styles from './InterestAccountFundTransferModal.module.scss';

type AccountFundTransferModalProps = {
  visible: boolean;
  transferType: TransferType;
  currentBalance: string;
  currency: string;
  onClose: (added: boolean) => void;
};

const cn = withStyles(styles);

const InterestAccountFundTransferModal: React.FC<AccountFundTransferModalProps> = ({
  visible,
  transferType,
  currentBalance,
  currency,
  onClose,
}) => {
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances' });
  const [updatedBalance, setUpdatedBalance] = useState(currentBalance);
  const { postInterestAccountTransfer } = usePostInterestAccountTransfer();

  const transferTypeMap = {
    Fund: {
      accountType: 'Standard',
      label: t('addFunds'),
      tooltip: t('fundTooltip'),
      success: t('fundTransferSucess'),
      error: t('fundTransferError'),
    },
    Withdraw: {
      accountType: 'Premium',
      label: t('withdrawFunds'),
      tooltip: t('withdrawTooltip'),
      success: t('fundWithdrawSuccess'),
      error: t('fundWithdrawError'),
    },
  };

  const { accountType, label, tooltip, success, error } = transferTypeMap[transferType];

  const createTransaction = async (values: { Amount: string }) => {
    try {
      const res = await postInterestAccountTransfer({
        PaymentType: transferType,
        Amount: values.Amount,
      });

      if (!res.Success) {
        toast.neutral(error);
      } else {
        onClose(true);
        toast.success(success);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    setUpdatedBalance(currentBalance);
  }, [currentBalance]);

  return (
    <Modal isVisible={visible} enableBgClick={false}>
      <Card
        style={{
          padding: '32px',
        }}
      >
        <Formik
          initialValues={{
            Amount: '',
          }}
          onSubmit={createTransaction}
          validationSchema={validationSchema()}
          validateOnMount
        >
          {({ values, errors, touched, isValid, handleChange, handleBlur, handleSubmit, resetForm, validateForm }) => {
            useEffect(() => {
              if (!visible) {
                resetForm();
              } else {
                validateForm();
              }
            }, [visible]);

            return (
              <Box flx="col" gap="common" position="relative" width="450px">
                <Title level="4">{label}</Title>
                <CircleButton
                  icon="close"
                  size="large"
                  circleFill
                  className={cn('closeButton')}
                  onClick={() => {
                    onClose(false);
                  }}
                />

                <div className={cn('addFundsCard')}>
                  {t('availableAccountBalance', { accountType })}
                  <div className={cn('balance')}>
                    {currency} {formatAmount(updatedBalance)}
                  </div>
                </div>

                <ElementContainer
                  label={
                    <Box ai="center" gapSize="2px">
                      {label}
                      <span className={cn('required')}>*</span>
                      <Tooltip
                        icon="info"
                        position="right"
                        information={
                          <Box gapSize="8px" flx="col">
                            <Title level="6">{label}</Title>
                            <p className={cn('message')}>{tooltip}</p>
                          </Box>
                        }
                      />
                    </Box>
                  }
                  padding="0px 0px 8px"
                  isBold={false}
                  input={
                    <TextField
                      id="Amount"
                      variant="outlineShadow"
                      preset="currency"
                      name="Amount"
                      placeholder={`${currency} 0.00`}
                      value={values['Amount']}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={touched['Amount'] && errors['Amount'] ? errors['Amount'] : null}
                    />
                  }
                />
                <Button
                  colorScheme="primary"
                  disabled={!isValid}
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  {label}
                </Button>
              </Box>
            );
          }}
        </Formik>
      </Card>
    </Modal>
  );

  function validationSchema() {
    return Yup.object().shape({
      Amount: Yup.string()
        .test('max-balance', t('balanceError'), (value) => {
          const sanitizedValue = sanitizeToNumeric(value);
          return parseFloat(sanitizedValue || '0') <= parseFloat(currentBalance);
        })
        .required(t('requiredFieldError')),
    });
  }
};

export default InterestAccountFundTransferModal;
